/**
 * Content region swapper.
 *
 * Consumes a parsed HTML page and swaps its main content into
 * the live document. Also handles the SDX engine case where the
 * content region is null because the page renders its main area
 * via a mounted component - in that case we do not touch the DOM
 * but still update the title and drupalSettings so the next
 * render picks them up.
 *
 * Swap strategy:
 *
 * - SDX engine page detected: return true without mutating the
 *   content region. The caller annotates the parsed page with
 *   the extracted SDX page data so downstream processors can
 *   propagate it through the navigation controller. Title and
 *   drupalSettings still update because the shell (title tag,
 *   data attributes) may have changed.
 * - Standard Drupal page: detachBehaviors on the old region,
 *   replace its children with the new region's children (via
 *   replaceChildren so we keep DOM node identity rather than
 *   re-parsing innerHTML), reattach behaviors, wrap the whole
 *   thing in a View Transition when the browser supports it.
 * - Vite dev mode: return false so the caller falls back to a
 *   full browser load. The Vite HMR runtime cannot survive
 *   DOM replacement.
 */

import { extractSdxPageData } from './parse-page';
import { mergeDrupalSettings } from '@sdx/core/drupal-settings';
import type { ParsedPage } from './types';

export interface SwapContext {
  readonly contentSelector: string;
  readonly devMode: boolean;
}

export interface SwapResult {
  /** True when the DOM was successfully reconciled. */
  swapped: boolean;
  /** SDX engine page data extracted from the parsed document, if any. */
  sdxPageData: Record<string, unknown> | null;
}

export function swapContent(page: ParsedPage, ctx: SwapContext): SwapResult {
  const current = findContentRegion(ctx.contentSelector);

  // SDX engine pages have no content to swap - the content region
  // is rendered by the mounted framework component. We still need
  // to flush the incoming title and drupalSettings so the next
  // render sees the new shell state.
  const sdxPageData = extractSdxPageData(page.doc);
  if (sdxPageData) {
    applyShellUpdates(page);
    return { swapped: true, sdxPageData };
  }

  if (!current || !page.content) {
    return { swapped: false, sdxPageData: null };
  }

  applyShellUpdates(page);

  // Dev mode is a hard no-go for HTML swap: Vite HMR state is
  // attached to the current document, DOM replacement destroys
  // every HMR client handle and leaves the page in a half-alive
  // state that neither HMR nor SPA navigation can recover from.
  if (ctx.devMode) {
    return { swapped: false, sdxPageData: null };
  }

  const region = current as HTMLElement;
  const content = page.content as Element;

  try {
    Drupal.detachBehaviors(region, drupalSettings, 'unload');
  }
  catch {
    // Behavior detach errors are non-fatal: we still want to
    // swap the content so the page is not left stale.
  }

  const doSwap = (): void => {
    // Move parsed DOM children in directly rather than serializing
    // to HTML and re-parsing via innerHTML. Keeps event listeners
    // on the incoming nodes intact and avoids the double HTML
    // parse round-trip that innerHTML would force.
    region.replaceChildren(...Array.from(content.childNodes));
    Drupal.attachBehaviors(region, drupalSettings);
  };

  const startViewTransition = (
    document as Document & { startViewTransition?: (callback: () => void) => { ready: Promise<void> } }
  ).startViewTransition;
  if (typeof startViewTransition === 'function' && document.visibilityState !== 'hidden') {
    const transition = startViewTransition.call(document, doSwap);
    // A skipped animation still runs doSwap; it is not a failed navigation.
    void transition.ready.catch(() => undefined);
  }
  else {
    doSwap();
  }

  return { swapped: true, sdxPageData: null };
}

function findContentRegion(selectorChain: string): Element | null {
  for (const selector of selectorChain.split(',')) {
    const match = document.querySelector(selector.trim());
    if (match) return match;
  }
  return null;
}

function applyShellUpdates(page: ParsedPage): void {
  if (page.title) {
    document.title = page.title;
  }
  if (page.settings) {
    mergeDrupalSettings(
      drupalSettings as unknown as Record<string, unknown>,
      page.settings,
    );
  }
}
